import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Sparkles, Loader2, Users, MessageSquare, ListChecks, Clock, MapPin, RefreshCw,
} from 'lucide-react';
import SegmentsPanel from './SegmentsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATE_BADGES = {
  never_visited: 'bg-gray-100 text-gray-700 border-gray-200',
  building_presence: 'bg-amber-50 text-amber-800 border-amber-200',
  relationships_active: 'bg-green-50 text-green-800 border-green-200',
  resting: 'bg-blue-50 text-blue-800 border-blue-200',
};

const fmtSince = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return null; }
};

const PilotDashboard = ({ canWrite = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeStreetId, setActiveStreetId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/pilot`, { withCredentials: true });
      setData(r.data);
      // Default to first pilot street so segments are visible immediately
      const pilot = r.data?.active_pilot;
      if (pilot?.streets?.length && !activeStreetId) {
        setActiveStreetId(pilot.streets[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load the pilot dashboard.');
    } finally { setLoading(false); }
  }, [activeStreetId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-8" data-testid="pilot-dashboard-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  if (!data?.active_pilot) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 text-center" data-testid="pilot-empty">
        <Sparkles className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
        <p className="text-sm text-[#0A0A0A] font-semibold">No pilot zone yet.</p>
        <p className="text-xs text-[#4B5563] mt-1 max-w-md mx-auto">
          To start, go to <strong>Zones &amp; Streets</strong>, create a zone (e.g. <em>&quot;East New York Pilot&quot;</em>),
          then turn on the <strong>&quot;Pilot zone&quot;</strong> toggle on the zone form. Mark one street as
          the pilot street (e.g. <em>&quot;108th Avenue&quot;</em>) and you&apos;ll see it here.
        </p>
      </div>
    );
  }

  const pilot = data.active_pilot;
  const labels = data.coverage_state_labels || {};
  const tally = pilot.summary?.coverage_tally || {};
  const segmentsForActiveStreet = (pilot.segments || []).filter(
    (s) => !activeStreetId || s.street_id === activeStreetId,
  );

  return (
    <div className="space-y-4" data-testid="pilot-dashboard">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#FFF8F8] to-white border border-[#BA1826]/30 rounded-lg p-4" data-testid="pilot-hero">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#BA1826] inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Active pilot
            </p>
            <p className="font-heading text-xl font-bold text-[#0A0A0A] mt-0.5" data-testid="pilot-zone-name">{pilot.zone.name}</p>
            {(pilot.zone.neighborhood || pilot.zone.city || pilot.zone.state) && (
              <p className="text-xs text-[#4B5563] inline-flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" />
                {[pilot.zone.neighborhood, pilot.zone.city, pilot.zone.state].filter(Boolean).join(' · ')}
              </p>
            )}
            {pilot.zone.description && (
              <p className="text-xs text-[#4B5563] mt-2 italic">&ldquo;{pilot.zone.description}&rdquo;</p>
            )}
          </div>
          <button onClick={load}
            className="text-[11px] px-2 py-1 text-[#4B5563] hover:text-[#0A0A0A] inline-flex items-center gap-1 border border-[#E5E7EB] rounded"
            data-testid="pilot-refresh-btn">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
        {error && <p className="text-[11px] text-red-700 mt-2">{error}</p>}
      </div>

      {/* Summary grid — quiet metrics, no completion %s */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="pilot-summary-grid">
        <SummaryTile label="Neighbors engaged" value={pilot.summary.neighbors_engaged}
          Icon={Users} testid="pilot-stat-neighbors" />
        <SummaryTile label="Conversations · last 30d" value={pilot.summary.conversations_last_30d}
          Icon={MessageSquare} testid="pilot-stat-conversations" />
        <SummaryTile label="Follow-ups pending" value={pilot.summary.follow_ups_pending}
          Icon={ListChecks} testid="pilot-stat-followups" amber={pilot.summary.follow_ups_pending > 0} />
        <SummaryTile label="Segments being worked" value={pilot.summary.active_segment_count}
          subValue={`of ${pilot.summary.segment_count}`}
          Icon={Sparkles} testid="pilot-stat-segments" />
      </div>

      {/* Coverage state ladder — counts, no percentages */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="pilot-state-ladder">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-2">Where each segment is right now</p>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(labels).map(([k, v]) => (
            <span key={k}
              className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${STATE_BADGES[k] || STATE_BADGES.never_visited}`}>
              {v} · {tally[k] || 0}
            </span>
          ))}
        </div>
        <p className="text-[10px] text-[#9CA3AF] italic mt-2">
          Doctrine note: relationships don&apos;t &ldquo;complete.&rdquo; <strong>Relationships Active</strong> means we&apos;re
          showing up consistently and neighbors know us — that&apos;s the win.
        </p>
      </div>

      {/* Streets nav */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="pilot-streets-nav">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-2">Pilot streets</p>
        {pilot.streets.length === 0 ? (
          <p className="text-[11px] text-[#9CA3AF] italic">
            No street has been marked as the pilot street yet. Edit a street in <strong>Zones &amp; Streets</strong>
            and turn on the &ldquo;Pilot street&rdquo; toggle.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {pilot.streets.map((s) => (
              <button key={s.id} onClick={() => setActiveStreetId(s.id)}
                className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${
                  activeStreetId === s.id
                    ? 'bg-[#BA1826] text-white border-[#BA1826]'
                    : 'bg-white text-[#0A0A0A] border-[#E5E7EB] hover:border-[#BA1826]/40'
                }`}
                data-testid={`pilot-street-tab-${s.id}`}>
                {s.name}
                <span className="ml-1.5 text-[10px] opacity-75">{s.segment_count} seg</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Active street's segments */}
      {activeStreetId && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid={`pilot-street-segments-${activeStreetId}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">
              Segments — {pilot.streets.find(s => s.id === activeStreetId)?.name || ''}
            </p>
          </div>
          {segmentsForActiveStreet.length === 0 ? (
            <SegmentsPanel streetId={activeStreetId} canWrite={canWrite} />
          ) : (
            <>
              {/* Per-segment cards with last-visit + counts (richer than the
                  bare SegmentsPanel — that one lives in Zones & Streets) */}
              <ul className="space-y-2">
                {segmentsForActiveStreet.map((s) => (
                  <li key={s.id}
                    className={`border rounded p-2 ${s.is_active ? 'border-[#E5E7EB] bg-white' : 'border-[#F1F5F9] bg-[#FAFBFC] opacity-70'}`}
                    data-testid={`pilot-segment-card-${s.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-[#0A0A0A] inline-flex items-center gap-1.5 flex-wrap">
                          {s.label}
                          {(s.range_start || s.range_end) && (
                            <span className="text-[10px] text-[#4B5563] font-normal inline-flex items-center gap-1">
                              <MapPin className="w-2.5 h-2.5" />
                              {s.range_start && s.range_end ? `${s.range_start} → ${s.range_end}` : s.range_start || s.range_end}
                            </span>
                          )}
                          <span className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${STATE_BADGES[s.coverage_state] || STATE_BADGES.never_visited}`}>
                            {s.coverage_state_label}
                          </span>
                        </p>
                        {s.assigned_volunteer_label && (
                          <p className="text-[11px] text-[#4B5563] mt-0.5">
                            Crew: <strong>{s.assigned_volunteer_label}</strong>
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <div className="text-center bg-[#FAFBFC] border border-[#E5E7EB] rounded p-1.5">
                        <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Last visit</p>
                        <p className="text-[11px] font-semibold text-[#0A0A0A] inline-flex items-center gap-1 mt-0.5">
                          <Clock className="w-2.5 h-2.5" />{fmtSince(s.last_visit_at) || 'No visits yet'}
                        </p>
                      </div>
                      <div className="text-center bg-[#FAFBFC] border border-[#E5E7EB] rounded p-1.5">
                        <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Conversations</p>
                        <p className="text-base font-heading font-bold text-[#0A0A0A]">{s.conversation_count}</p>
                      </div>
                      <div className={`text-center border rounded p-1.5 ${s.follow_ups_pending > 0 ? 'bg-amber-50 border-amber-200' : 'bg-[#FAFBFC] border-[#E5E7EB]'}`}>
                        <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">Follow-ups</p>
                        <p className={`text-base font-heading font-bold ${s.follow_ups_pending > 0 ? 'text-amber-800' : 'text-[#0A0A0A]'}`}>{s.follow_ups_pending}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <p className="text-[10px] text-[#9CA3AF] italic text-center pt-2">
        Goal: build consistent community presence and relationship continuity along the pilot — not to &ldquo;finish&rdquo; anything.
      </p>
    </div>
  );
};

const SummaryTile = ({ label, value, subValue, Icon, testid, amber = false }) => (
  <div className={`border rounded-lg p-2.5 ${amber ? 'bg-amber-50 border-amber-200' : 'bg-white border-[#E5E7EB]'}`}
    data-testid={testid}>
    <div className="flex items-center justify-between mb-1">
      <span className="text-[9px] uppercase tracking-wider font-bold text-[#4B5563]">{label}</span>
      <Icon className={`w-3.5 h-3.5 ${amber ? 'text-amber-700' : 'text-[#BA1826]'}`} />
    </div>
    <p className={`text-xl font-heading font-bold ${amber ? 'text-amber-800' : 'text-[#0A0A0A]'}`}>
      {value}
      {subValue && <span className="text-[10px] text-[#9CA3AF] font-normal ml-1">{subValue}</span>}
    </p>
  </div>
);

export default PilotDashboard;
